import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getPriceBookDetails } from "../services";
import { PriceBookDetail } from "../types/price-book.type";
import { SYSTEM_SETTING_KEY } from "../types/system-setting.type";
import { featureAccess } from "../permissions/access";
import { useUserRole } from "../utils/hooks/useUserRole";
import {
  findTextSetting,
  useSystemSettings,
} from "../utils/hooks/useSystemSettings";
import { numberWithCommas } from "../utils";
import { formatDateTimeBkk } from "../utils/date";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import PriceBookDetails from "../components/Product/PriceBook/Details";
import PriceBookHeaderActions from "../components/Product/PriceBook/Details/HeaderActions";
import { findReservedBookKind } from "../components/Product/PriceBook/Details/reservedBook";
import { usePricebookDraftDiff } from "../components/Product/PriceBook/Details/usePricebookDraftDiff";
import PriceBookDrawer from "../components/Product/PriceBook/PriceBookDrawer";
import PriceBookTypeChip from "../components/Product/PriceBook/TypeChip";
import ValidityNote from "../components/Product/PriceBook/ValidityNote";
import StatusPill from "../components/Product/shared/StatusPill";
import { DRAFT_LABEL } from "../components/Product/PriceBook/constants";
import { PRICE_BOOK_LIST_PATH } from "../components/Product/shared/constants";

const EMPTY_PLACEHOLDER = "-";

/** "สร้างโดย … เมื่อ … · แก้ไขล่าสุด … โดย …"; rows older than the audit names have no names. */
const buildAuditLine = (priceBook: PriceBookDetail): string => {
  const createdBy = priceBook.created_by_name
    ? ` โดย ${priceBook.created_by_name}`
    : "";
  const updatedBy = priceBook.updated_by_name
    ? ` โดย ${priceBook.updated_by_name}`
    : "";
  const createdAt = formatDateTimeBkk(priceBook.created_at) || EMPTY_PLACEHOLDER;
  const updatedAt = formatDateTimeBkk(priceBook.updated_at) || EMPTY_PLACEHOLDER;
  return `สร้าง ${createdAt}${createdBy} · แก้ไขล่าสุด ${updatedAt}${updatedBy}`;
};

export default function PriceBookDetailsPage() {
  const { pricebookId } = useParams<{ pricebookId: string }>();
  const navigate = useNavigate();
  const role = useUserRole();
  const canEditCatalogue = featureAccess.productWrite(role);
  const { settings, loading: settingsLoading } = useSystemSettings();

  const [priceBook, setPriceBook] = useState<PriceBookDetail>();
  const [headerVersion, setHeaderVersion] = useState<number>(0);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [refusalMessage, setRefusalMessage] = useState<string>("");

  // Only a draft is compared with its source, and only for a role that may apply it.
  const canApplyDraft = Boolean(priceBook?.is_draft) && canEditCatalogue;
  const draftDiff = usePricebookDraftDiff(
    priceBook?.uuid ?? "",
    canApplyDraft,
    priceBook?.updated_at ?? "",
  );

  const getDetails = useCallback(async (uuid: string) => {
    const {
      data: { data },
    } = await getPriceBookDetails(uuid);
    setPriceBook(data);
  }, []);

  useEffect(() => {
    if (pricebookId) getDetails(pricebookId);
  }, [pricebookId, headerVersion, getDetails]);

  // A message about one book must not stay on screen once another is open.
  useEffect(() => {
    setRefusalMessage("");
  }, [pricebookId]);

  const reloadHeader = useCallback(
    () => setHeaderVersion((prev) => prev + 1),
    [],
  );

  const handleOpenEditDrawer = () => setOpenDrawer(true);
  const handleCloseDrawer = () => setOpenDrawer(false);
  const handleDismissRefusal = () => setRefusalMessage("");
  // This page describes one pricebook, so once it is deleted there is nothing to read.
  const handleDeleted = () => navigate(PRICE_BOOK_LIST_PATH);

  if (!priceBook) return null;

  const reservedBook = findReservedBookKind(settings, priceBook.code);
  const canCopyAsDraft = !settingsLoading && !reservedBook;
  // The API refuses a validity on a draft and on the books the system settings name.
  const canSetValidity = !settingsLoading && !reservedBook && !priceBook.is_draft;
  const draftBarDiff = canApplyDraft ? draftDiff : null;
  const facePriceCode = findTextSetting(
    settings,
    SYSTEM_SETTING_KEY.pricebookFacePriceCode,
  );

  return (
    <>
      <Box sx={{ padding: "8px 0" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          mb={2}
        >
          <Stack direction="row" alignItems="center">
            <Link
              to={PRICE_BOOK_LIST_PATH}
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                สมุดราคา
              </Typography>
            </Link>
            <Typography variant="body1" sx={{ mx: 1 }}>
              /
            </Typography>
            <Typography variant="body1" color="primary">
              {priceBook.name}
            </Typography>
          </Stack>
          {canEditCatalogue && (
            <PriceBookHeaderActions
              priceBook={priceBook}
              canCopyAsDraft={canCopyAsDraft}
              hasUnsavedChanges={hasUnsavedChanges}
              draftDiff={draftDiff}
              onOpenEditDrawer={handleOpenEditDrawer}
              onChanged={reloadHeader}
              onDraftDeleted={handleDeleted}
              onRefused={setRefusalMessage}
            />
          )}
        </Stack>

        {refusalMessage && (
          <Alert severity="warning" onClose={handleDismissRefusal} sx={{ mb: 2 }}>
            {refusalMessage}
          </Alert>
        )}

        <Paper sx={{ mb: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            gap={2}
          >
            <Box>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="h6" fontWeight={700}>
                  {priceBook.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {priceBook.code}
                </Typography>
                {priceBook.is_draft && (
                  <Chip label={DRAFT_LABEL} size="small" color="warning" />
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {buildAuditLine(priceBook)}
              </Typography>
              <ValidityNote validity={priceBook} facePriceCode={facePriceCode} />
            </Box>
            <Stack direction="row" alignItems="center" gap={2}>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="caption" fontWeight={600} display="block">
                  สินค้าที่เปิดขาย
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  {numberWithCommas(priceBook.product_count)} รายการ
                </Typography>
              </Box>
              <PriceBookTypeChip type={priceBook.type} />
              <StatusPill status={priceBook.status} />
            </Stack>
          </Stack>
        </Paper>

        {/* Keyed on the book: its tab, edits and dialogs belong to it, and an apply opens the
            source on the tab that shows the result. */}
        <PriceBookDetails
          key={priceBook.uuid}
          priceBook={priceBook}
          canEdit={canEditCatalogue}
          reservedBook={reservedBook}
          settingsLoading={settingsLoading}
          draftDiff={draftBarDiff}
          onChanged={reloadHeader}
          onDirtyChange={setHasUnsavedChanges}
        />
      </Box>

      <PriceBookDrawer
        open={openDrawer}
        mode="edit"
        priceBookUuid={priceBook.uuid}
        canSetValidity={canSetValidity}
        onClose={handleCloseDrawer}
        onSaved={reloadHeader}
        onDeleted={handleDeleted}
      />
    </>
  );
}
